import { useState, useEffect, useRef, useCallback } from 'react';
import { camerasAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  WifiIcon,
  CameraIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckBadgeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

const DISCOVERY_METHOD_BADGES = {
  onvif: { label: 'ONVIF', className: 'bg-emerald-100 text-emerald-700' },
  port_scan: { label: 'Port Scan', className: 'bg-blue-100 text-blue-700' },
  both: { label: 'Both', className: 'bg-purple-100 text-purple-700' },
};

export default function DiscoverCamerasModal({ isOpen, onClose, siteId, onCameraAdded }) {
  const [scanning, setScanning] = useState(false);
  const [scanSessionId, setScanSessionId] = useState(null);
  const [discoveredCameras, setDiscoveredCameras] = useState([]);
  const [scanStatus, setScanStatus] = useState(null); // 'scanning', 'completed', 'error'
  const [scanProgress, setScanProgress] = useState(0);
  const [scanError, setScanError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(null);
  const [cameraDetails, setCameraDetails] = useState({});
  const [formData, setFormData] = useState({
    subnet: '',
    portScan: true,
    username: '',
    password: '',
    timeout: 30,
  });
  const pollIntervalRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((sessionId) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await camerasAPI.getDiscoveryScanStatus(sessionId);
        const data = res.data;
        setDiscoveredCameras(data.cameras || []);
        setScanProgress(data.progress || 0);
        setScanStatus(data.status);

        if (data.status === 'completed' || data.status === 'error') {
          stopPolling();
          setScanning(false);
          if (data.status === 'error') {
            setScanError(data.error || 'Scan failed');
          }
        }
      } catch (err) {
        stopPolling();
        setScanning(false);
        setScanStatus('error');
        setScanError('Failed to get scan status');
      }
    }, 2000);
  }, [stopPolling]);

  const handleStartScan = async () => {
    setScanning(true);
    setScanError(null);
    setDiscoveredCameras([]);
    setScanStatus('scanning');
    setScanProgress(0);

    try {
      const params = { siteId };
      if (formData.subnet) params.subnet = formData.subnet;
      params.enablePortScan = formData.portScan;
      if (formData.username) params.username = formData.username;
      if (formData.password) params.password = formData.password;
      params.timeout = formData.timeout;

      const res = await camerasAPI.startDiscoveryScan(params);
      const sessionId = res.data?.scanSessionId;
      if (!sessionId) {
        throw new Error('No scan session ID returned');
      }
      setScanSessionId(sessionId);
      startPolling(sessionId);
    } catch (err) {
      setScanning(false);
      setScanStatus('error');
      setScanError(err.response?.data?.error || err.message || 'Failed to start scan');
    }
  };

  const handleGetInfo = async (cam) => {
    const key = `${cam.ip}:${cam.port}`;
    setFetchingInfo(key);
    try {
      const res = await camerasAPI.getOnvifInfo(cam.ip, cam.port, formData.username || 'admin', formData.password || '');
      setCameraDetails(prev => ({ ...prev, [key]: res.data }));
    } catch (err) {
      setCameraDetails(prev => ({ ...prev, [key]: { error: 'Failed to get camera info. Check credentials.' } }));
    } finally {
      setFetchingInfo(null);
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('RTSP URL copied to clipboard');
  };

  const handleAddCamera = (cam) => {
    const key = `${cam.ip}:${cam.port}`;
    const details = cameraDetails[key];
    onCameraAdded({
      ip: cam.ip,
      port: cam.port,
      name: cam.name || cam.manufacturer ? `${cam.manufacturer || ''} ${cam.model || ''}`.trim() : `Camera ${cam.ip}`,
      manufacturer: cam.manufacturer || details?.manufacturer,
      model: cam.model || details?.model,
      rtspUrl: details?.rtsp_urls?.[0] || cam.rtspUrls?.[0] || `rtsp://${cam.ip}:554/stream`,
      rtspUrls: details?.rtsp_urls || cam.rtspUrls || [],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <WifiIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="modal-title">Discover Cameras</h3>
                <p className="text-sm text-slate-500">Scan the network for ONVIF cameras and open RTSP ports</p>
              </div>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body space-y-4">
            {/* Advanced Options Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                {showAdvanced ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="mt-3 p-4 bg-slate-50 rounded-xl space-y-3">
                  {/* Subnet */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Subnet (optional)</label>
                    <input
                      type="text"
                      value={formData.subnet}
                      onChange={(e) => setFormData(prev => ({ ...prev, subnet: e.target.value }))}
                      placeholder="e.g. 192.168.1.0/24"
                      className="input w-full"
                    />
                  </div>

                  {/* Port Scan Toggle */}
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Enable Port Scanning</label>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, portScan: !prev.portScan }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.portScan ? 'bg-primary-500' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.portScan ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Credentials */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="admin"
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Password"
                        className="input w-full"
                      />
                    </div>
                  </div>

                  {/* Timeout Slider */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Timeout: {formData.timeout}s
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="60"
                      value={formData.timeout}
                      onChange={(e) => setFormData(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
                      className="w-full accent-primary-500"
                    />
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>5s</span>
                      <span>60s</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Scan Button & Progress */}
            <div className="flex items-center justify-between">
              <button
                onClick={handleStartScan}
                disabled={scanning}
                className="btn btn-primary flex items-center gap-2"
              >
                {scanning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    Scanning Network...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="w-5 h-5" />
                    Scan for Cameras
                  </>
                )}
              </button>
              <div className="flex items-center gap-3">
                {scanning && (
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all duration-500"
                        style={{ width: `${scanProgress}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500">{Math.round(scanProgress)}%</span>
                  </div>
                )}
                {discoveredCameras.length > 0 && (
                  <span className="text-sm text-slate-500">{discoveredCameras.length} camera(s) found</span>
                )}
              </div>
            </div>

            {/* Error */}
            {scanError && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                {scanError}
              </div>
            )}

            {/* Results */}
            {discoveredCameras.length > 0 && (
              <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                {discoveredCameras.map((cam, idx) => {
                  const key = `${cam.ip}:${cam.port}`;
                  const details = cameraDetails[key];
                  const methodBadge = DISCOVERY_METHOD_BADGES[cam.discoveryMethod] || DISCOVERY_METHOD_BADGES.onvif;

                  return (
                    <div key={key || idx} className="glass rounded-xl p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {/* Thumbnail or placeholder */}
                          <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {cam.thumbnail ? (
                              <img
                                src={cam.thumbnail.startsWith('data:') ? cam.thumbnail : `data:image/jpeg;base64,${cam.thumbnail}`}
                                alt="Camera preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <CameraIcon className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">
                                {cam.name || `Camera ${cam.ip}`}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${methodBadge.className}`}>
                                {methodBadge.label}
                              </span>
                              {cam.alreadyConfigured && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                  <CheckBadgeIcon className="w-3.5 h-3.5" />
                                  Already Configured
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500 mt-0.5">
                              {cam.ip}:{cam.port}
                              {cam.manufacturer && ` \u2022 ${cam.manufacturer}`}
                              {cam.model && ` ${cam.model}`}
                            </div>
                            {/* Open ports */}
                            {cam.openPorts && cam.openPorts.length > 0 && (
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <span className="text-xs text-slate-400">Ports:</span>
                                {cam.openPorts.map((p) => (
                                  <span key={p} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                    {p}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleGetInfo(cam)}
                            disabled={fetchingInfo === key}
                            className="btn btn-secondary text-xs px-3 py-1"
                          >
                            {fetchingInfo === key ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-400/30 border-t-slate-400 mr-1 inline-block"></div>
                                Loading...
                              </>
                            ) : (
                              <>
                                <InformationCircleIcon className="w-4 h-4 inline mr-1" />
                                Get ONVIF Info
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleAddCamera(cam)}
                            className="btn btn-primary text-xs px-3 py-1 flex items-center gap-1"
                            disabled={!siteId || cam.alreadyConfigured}
                            title={!siteId ? 'Select a site first' : cam.alreadyConfigured ? 'Camera already configured' : 'Add this camera'}
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Camera
                          </button>
                        </div>
                      </div>

                      {/* RTSP URLs */}
                      {(cam.rtspUrls?.length > 0 || details?.rtsp_urls?.length > 0) && (
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-xs font-medium text-slate-600">RTSP URLs:</span>
                          {(details?.rtsp_urls || cam.rtspUrls || []).map((url, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 mt-1">
                              <code className="text-xs text-slate-600 break-all flex-1">{url}</code>
                              <button
                                onClick={() => handleCopyUrl(url)}
                                className="icon-btn p-1 flex-shrink-0"
                                title="Copy URL"
                              >
                                <ClipboardDocumentIcon className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Device details from ONVIF info */}
                      {details && !details.error && (
                        <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 space-y-1">
                          <div><span className="font-medium">Manufacturer:</span> {details.manufacturer || 'Unknown'}</div>
                          <div><span className="font-medium">Model:</span> {details.model || 'Unknown'}</div>
                          <div><span className="font-medium">Firmware:</span> {details.firmware || 'Unknown'}</div>
                          {details.profiles?.length > 0 && (
                            <div><span className="font-medium">Profiles:</span> {details.profiles.map(p => p.name).join(', ')}</div>
                          )}
                        </div>
                      )}
                      {details?.error && (
                        <div className="text-xs text-red-500">{details.error}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {!scanning && discoveredCameras.length === 0 && !scanError && (
              <div className="text-center py-8 text-slate-400">
                <WifiIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Click "Scan for Cameras" to discover ONVIF cameras on your network</p>
                <p className="text-xs mt-1">Supports ONVIF discovery and RTSP port scanning</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
